import React, { useState } from 'react'
import { api } from '../api'

const STATUT_LABELS = {
  en_cours_commercial: { label: 'En cours — Commercial', color: '#534AB7' },
  en_attente_facilitateur: { label: 'À traiter', color: '#EF9F27' },
  en_cours_facilitateur: { label: 'En cours — Facilitateur', color: '#0F6E56' },
  termine: { label: 'Terminé', color: '#639922' },
}

const NIVEAU_LABELS = {
  tolérable: { label: '🟡 Tolérable', color: '#FAC775' },
  indésirable: { label: '🟠 Indésirable', color: '#EF9F27' },
  intolérable: { label: '🔴 Intolérable', color: '#E24B4A' },
}

export default function DossierDetail({ dossier: initialDossier, onBack }) {
  const [dossier, setDossier] = useState(initialDossier)
  const [activeTab, setActiveTab] = useState('infos')
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [jsonInput, setJsonInput] = useState('')
  const [jsonError, setJsonError] = useState('')
  const [saved, setSaved] = useState(false)

  const statut = STATUT_LABELS[dossier.statut] || { label: dossier.statut, color: '#888' }
  const ent = dossier.entreprise || {}

  const updateStatut = async (newStatut) => {
    setLoading(true)
    try {
      const updated = await api.updateDossier(dossier.id, { statut: newStatut })
      setDossier(updated)
    } catch (e) {
      alert('Erreur : ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  const handleImportJson = async () => {
    setJsonError('')
    let parsed
    try {
      parsed = JSON.parse(jsonInput)
    } catch {
      setJsonError('JSON invalide — vérifiez la syntaxe')
      return
    }
    setLoading(true)
    try {
      const payload = {
        unites_de_travail: parsed.unites_de_travail || [],
        evaluation_risques: parsed.evaluation_risques || [],
        plan_action: parsed.plan_action || [],
        synthese: parsed.synthese || {},
        lead_paie: parsed.lead_paie || { qualifie: false },
        statut: 'en_cours_facilitateur',
      }
      const updated = await api.updateDossier(dossier.id, payload)
      setDossier(updated)
      setJsonInput('')
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      setActiveTab('risques')
    } catch (e) {
      setJsonError('Erreur sauvegarde : ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      await api.generateDuer(dossier.id)
      const updated = await api.getDossier(dossier.id)
      setDossier(updated)
    } catch (e) {
      alert('Erreur génération : ' + e.message)
    } finally {
      setGenerating(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce dossier ? Cette action est irréversible.')) return
    try {
      await api.deleteDossier(dossier.id)
      onBack()
    } catch (e) {
      alert('Erreur suppression')
    }
  }

  const canGenerate = dossier.evaluation_risques && dossier.evaluation_risques.length > 0

  return (
    <div className="dossier-detail">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}>← Tableau de bord</button>
        <div className="page-header-right">
          <span className="statut-badge" style={{ background: statut.color }}>
            {statut.label}
          </span>
        </div>
      </div>

      <div className="dossier-title-row">
        <div>
          <h1 className="page-title">{ent.raison_sociale || '—'}</h1>
          <p className="dossier-subtitle">
            {ent.forme_juridique} · SIREN {ent.siren} · {ent.effectif} salarié(s)
            {dossier.facilitateur && ` · ${dossier.facilitateur}`}
          </p>
        </div>
        <div className="dossier-actions">
          {dossier.statut === 'en_cours_commercial' && (
            <button className="btn btn-warning" onClick={() => updateStatut('en_attente_facilitateur')} disabled={loading}>
              Passer au facilitateur
            </button>
          )}
          {dossier.statut === 'en_attente_facilitateur' && (
            <button className="btn btn-primary" onClick={() => updateStatut('en_cours_facilitateur')} disabled={loading}>
              Prendre en charge
            </button>
          )}
          {canGenerate && (
            <button className="btn btn-success" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Génération...' : '⬇ Télécharger DUER'}
            </button>
          )}
          <button className="btn btn-danger-outline" onClick={handleDelete}>Supprimer</button>
        </div>
      </div>

      <div className="tabs">
        {[
          { key: 'infos', label: 'Informations' },
          { key: 'ut', label: `Unités de travail (${(dossier.unites_de_travail || []).length})` },
          { key: 'risques', label: `Évaluation (${(dossier.evaluation_risques || []).length} UT)` },
          { key: 'plan', label: `Plan d'action (${(dossier.plan_action || []).length})` },
          { key: 'synthese', label: 'Synthèse' },
          { key: 'json', label: 'Import JSON' },
        ].map(tab => (
          <button
            key={tab.key}
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">

        {activeTab === 'infos' && (
          <div className="card">
            <h2 className="card-title">Informations entreprise</h2>
            <div className="info-grid">
              {[
                ['Raison sociale', ent.raison_sociale],
                ['Forme juridique', ent.forme_juridique],
                ['SIREN', ent.siren],
                ['SIRET', ent.siret],
                ['Adresse', ent.adresse],
                ['Code NAF', `${ent.code_naf || '—'} — ${ent.libelle_naf || ''}`],
                ['Dirigeant', ent.dirigeant],
                ['Effectif', ent.effectif ? `${ent.effectif} salarié(s)` : '—'],
                ['Médecin du travail', ent.medecin_travail],
                ['Référent sécurité', ent.referent_securite || 'Non désigné'],
                ['CSE', ent.cse ? 'Oui' : (ent.effectif < 11 ? 'Non concerné' : 'Non')],
                ['Date mise à jour', ent.date_mise_a_jour],
              ].map(([label, value]) => (
                <div key={label} className="info-row">
                  <span className="info-label">{label}</span>
                  <span className="info-value">{value || '—'}</span>
                </div>
              ))}
            </div>
            {dossier.lead_paie?.qualifie && (
              <div className="lead-paie-banner">
                <strong>Lead Paie qualifié</strong>
                {dossier.lead_paie.signal && <span> · Signal : {dossier.lead_paie.signal}</span>}
                {dossier.lead_paie.note && <span> · {dossier.lead_paie.note}</span>}
              </div>
            )}
          </div>
        )}

        {activeTab === 'ut' && (
          <div className="card">
            <h2 className="card-title">Unités de travail</h2>
            {(!dossier.unites_de_travail || dossier.unites_de_travail.length === 0) ? (
              <p className="empty-state">Aucune unité de travail enregistrée.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Unité de travail</th>
                    <th>Effectif</th>
                    <th>Activités</th>
                  </tr>
                </thead>
                <tbody>
                  {dossier.unites_de_travail.map(ut => (
                    <tr key={ut.id}>
                      <td><strong>{ut.nom}</strong></td>
                      <td>{ut.effectif}</td>
                      <td>{(ut.activites || []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'risques' && (
          <div>
            {(!dossier.evaluation_risques || dossier.evaluation_risques.length === 0) ? (
              <div className="card">
                <p className="empty-state">Aucune évaluation enregistrée. Importez le JSON depuis l'onglet "Import JSON".</p>
              </div>
            ) : (
              dossier.evaluation_risques.map(ut_eval => (
                <div key={ut_eval.ut_id} className="card card--mt">
                  <h2 className="card-title">UT : {ut_eval.ut_nom}</h2>
                  {(ut_eval.familles || []).map(famille => {
                    const maxCriticite = Math.max(...(famille.dangers || []).map(d => d.criticite || 0))
                    const niveauMax = maxCriticite >= 10 ? 'intolérable' : maxCriticite >= 4 ? 'indésirable' : 'tolérable'
                    return (
                      <div key={famille.id} className="famille-block">
                        <div className="famille-header">
                          <span className="famille-nom">{famille.nom}</span>
                          <span className="famille-badge" style={{ background: NIVEAU_LABELS[niveauMax]?.color }}>
                            {NIVEAU_LABELS[niveauMax]?.label}
                          </span>
                          {famille.obligatoire && <span className="badge-obligatoire">Obligatoire</span>}
                        </div>

                        {(famille.dangers || []).length > 0 && (
                          <table className="data-table data-table--sm">
                            <thead>
                              <tr>
                                <th>Danger identifié</th>
                                <th className="col-center">P</th>
                                <th className="col-center">G</th>
                                <th className="col-center">Criticité</th>
                              </tr>
                            </thead>
                            <tbody>
                              {famille.dangers.map((d, i) => {
                                const niv = NIVEAU_LABELS[d.niveau]
                                return (
                                  <tr key={i}>
                                    <td>{d.description}</td>
                                    <td className="col-center">{d.probabilite}</td>
                                    <td className="col-center">{d.gravite}</td>
                                    <td className="col-center">
                                      <span className="criticite-badge" style={{ background: niv?.color }}>
                                        {d.criticite}
                                      </span>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        )}

                        {(famille.actions_realisees || []).length > 0 && (
                          <div className="actions-block actions-block--done">
                            <strong>Actions réalisées :</strong>
                            <ul>{famille.actions_realisees.map((a, i) => <li key={i}>{a}</li>)}</ul>
                          </div>
                        )}
                        {(famille.actions_a_mettre_en_oeuvre || []).length > 0 && (
                          <div className="actions-block actions-block--todo">
                            <strong>Actions à mettre en œuvre :</strong>
                            <ul>{famille.actions_a_mettre_en_oeuvre.map((a, i) => <li key={i}>{a}</li>)}</ul>
                          </div>
                        )}
                        {famille.observations && (
                          <div className="observations-block">
                            <strong>Observations : </strong>{famille.observations}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'plan' && (
          <div className="card">
            <h2 className="card-title">Plan d'action de prévention</h2>
            {(!dossier.plan_action || dossier.plan_action.length === 0) ? (
              <p className="empty-state">Aucune action enregistrée.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Famille</th>
                    <th>Unité de travail</th>
                    <th>Action</th>
                    <th>Priorité</th>
                    <th>Responsable</th>
                  </tr>
                </thead>
                <tbody>
                  {dossier.plan_action.map((a, i) => {
                    const prioriteColors = { court_terme: '#E24B4A', moyen_terme: '#EF9F27', long_terme: '#639922' }
                    const prioriteLabels = { court_terme: 'Court terme', moyen_terme: 'Moyen terme', long_terme: 'Long terme' }
                    return (
                      <tr key={i}>
                        <td>{a.famille}</td>
                        <td>{a.ut_nom}</td>
                        <td>{a.action}</td>
                        <td>
                          <span className="priorite-badge" style={{ background: prioriteColors[a.priorite] }}>
                            {prioriteLabels[a.priorite] || a.priorite}
                          </span>
                        </td>
                        <td>{a.responsable}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'synthese' && (
          <div className="card">
            <h2 className="card-title">Synthèse</h2>
            {!dossier.synthese || Object.keys(dossier.synthese).length === 0 ? (
              <p className="empty-state">Aucune synthèse enregistrée.</p>
            ) : (
              <div>
                {(dossier.synthese.points_forts || []).length > 0 && (
                  <div className="synthese-block synthese-block--success">
                    <h3>Points forts</h3>
                    <ul>{dossier.synthese.points_forts.map((p, i) => <li key={i}>{p}</li>)}</ul>
                  </div>
                )}
                {(dossier.synthese.axes_amelioration || []).length > 0 && (
                  <div className="synthese-block synthese-block--warning">
                    <h3>Axes d'amélioration</h3>
                    <ul>{dossier.synthese.axes_amelioration.map((a, i) => <li key={i}>{a}</li>)}</ul>
                  </div>
                )}
                {dossier.synthese.niveau_conformite && (
                  <p className="niveau-conformite">
                    Niveau de conformité : <strong>{dossier.synthese.niveau_conformite}</strong>
                  </p>
                )}
                {dossier.synthese.conclusion && (
                  <div className="conclusion-block">
                    <h3>Conclusion</h3>
                    <p>{dossier.synthese.conclusion}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'json' && (
          <div className="card">
            <h2 className="card-title">Import JSON depuis Claude</h2>
            <p className="card-subtitle">
              Collez ici le JSON généré par Claude à la fin du questionnaire DUER.
              Les données seront intégrées au dossier et le DUER .docx pourra être téléchargé.
            </p>
            <textarea
              className="json-textarea"
              value={jsonInput}
              onChange={e => setJsonInput(e.target.value)}
              placeholder={'{\n  "unites_de_travail": [...],\n  "evaluation_risques": [...],\n  "plan_action": [...],\n  "synthese": {...},\n  "lead_paie": {...}\n}'}
              spellCheck={false}
            />
            {jsonError && <p className="form-error">{jsonError}</p>}
            {saved && <p className="form-success">Données importées avec succès ✓</p>}
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleImportJson} disabled={loading || !jsonInput.trim()}>
                {loading ? 'Import...' : 'Importer et sauvegarder'}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
